import React from "react";
import "./Card.css";
import aboutGif from "../Assets/about-fabric.gif";
import { Link } from "react-router-dom";
import useSiteContent from "../../utils/useSiteContent";
import { ABOUT_FABRIC_DEFAULTS } from "../../Constants/aboutFabricContent";

// Shows `text` with the first occurrence of `highlight` in the brand colour
const Highlighted = ({ text, highlight }) => {
  const at = highlight ? text.indexOf(highlight) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <span className="text-[var(--brand-primary-700)]">{highlight}</span>
      {text.slice(at + highlight.length)}
    </>
  );
};

const Card = () => {
  // Text and image are editable from Admin → HomePage → About Our Fabric
  const c = useSiteContent("about-fabric", ABOUT_FABRIC_DEFAULTS);
  return (
    <div
      className="relative overflow-hidden px-6 py-12 sm:px-10 md:px-16 mb-6"
      style={{ background: "linear-gradient(135deg, var(--brand-primary-900) 0%, var(--brand-primary-700) 50%, var(--brand-primary-600) 100%)" }}
    >
      {/* Decorative orbs */}
      <div
        className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, var(--brand-primary-200) 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-16 -left-16 w-60 h-60 rounded-full opacity-15 pointer-events-none"
        style={{ background: "radial-gradient(circle, var(--brand-primary-200) 0%, transparent 70%)" }}
      />
      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10">

        {/* ── Left: GIF card ── */}
        <div className="w-full lg:w-[44%] flex-shrink-0">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-[var(--brand-primary-100)]">
            {/* Card header */}
            <div className="px-5 py-3 border-b border-[var(--brand-primary-100)] flex items-center gap-2"
                 style={{ background: "linear-gradient(90deg, var(--brand-primary-100), var(--brand-secondary-100))" }}>
              <div className="w-2 h-2 rounded-full bg-[var(--brand-primary-400)]" />
              <div className="w-2 h-2 rounded-full bg-[var(--brand-primary-300)]" />
              <div className="w-2 h-2 rounded-full bg-[var(--brand-primary-200)]" />
              <span className="ml-2 text-xs font-semibold text-[var(--brand-primary-700)] uppercase tracking-widest">
                {c.processTitle}
              </span>
            </div>
            {/* Steps */}
            <div className="flex flex-col items-center gap-3 p-5">
              <div className="flex items-center gap-2 bg-[var(--brand-primary-50)] border border-[var(--brand-primary-200)] rounded-full px-4 py-1.5">
                <span className="w-5 h-5 rounded-full bg-[var(--brand-primary-600)] text-white text-xs flex items-center justify-center font-bold">1</span>
                <span className="text-sm font-semibold text-gray-800"><Highlighted text={c.step1Text} highlight={c.step1Highlight} /></span>
              </div>

              <img
                crossOrigin="anonymous"
                loading="lazy"
                src={c.image || aboutGif}
                alt="Custom fabric process"
                className="w-full max-w-[280px] h-[240px] object-cover rounded-xl shadow-md border border-[var(--brand-primary-100)]"
              />

              <div className="flex items-center gap-2 bg-[var(--brand-primary-50)] border border-[var(--brand-primary-200)] rounded-full px-4 py-1.5">
                <span className="w-5 h-5 rounded-full bg-[var(--brand-primary-600)] text-white text-xs flex items-center justify-center font-bold">2</span>
                <span className="text-sm font-semibold text-gray-800"><Highlighted text={c.step2Text} highlight={c.step2Highlight} /></span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Content ── */}
        <div className="w-full lg:w-[56%] flex flex-col gap-5 text-white">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 w-fit">
            <span className="text-xs font-semibold uppercase tracking-widest">{c.badge}</span>
          </div>

          {/* Heading */}
          <div className="w-fit">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Times New Roman', Serif" }}
            >
              {c.heading}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-2">
              <div className="h-px w-10 bg-white/30" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[var(--brand-primary-300)]" />
              <div className="h-px w-10 bg-white/30" />
            </div>
          </div>

          {/* Description */}
          <p className="text-white/80 text-sm sm:text-base leading-relaxed">
            {c.intro1}
          </p>
          <p className="text-white/70 text-sm leading-relaxed">
            {c.intro2}
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2">
            {c.pills.map((text) => (
              <span
                key={text}
                className="flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-medium px-4 py-1.5 rounded-full"
              >
                {text}
              </span>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 mt-1">
            <Link
              to="/explore-our-ideas"
              className="inline-flex items-center justify-center min-h-[48px] px-8 rounded-full text-sm font-semibold text-center bg-white text-[var(--brand-primary-700)] border-2 border-white shadow-lg hover:bg-[var(--brand-primary-50)] hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--brand-primary-700)] transition-all duration-200"
            >
              {c.button1Text}
            </Link>
            <a
              href={`https://wa.me/919789389489?text=${encodeURIComponent(c.whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center min-h-[48px] px-8 rounded-full text-sm font-semibold text-center text-white border-2 border-white/60 hover:bg-white hover:text-[var(--brand-primary-700)] hover:border-white hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--brand-primary-700)] transition-all duration-200"
            >
              {c.button2Text}
            </a>
          </div>
        </div>
      </div>

      {/* ── Full content sections ── */}
      <div className="relative max-w-6xl mx-auto mt-12 space-y-6">
        {/* Intro continuation */}
        <p className="text-white/75 text-sm sm:text-base leading-relaxed">
          {c.story}
        </p>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Bespoke Print Development */}
          <div className="bg-white/10 border border-white/15 rounded-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-bold text-white">{c.bespokeTitle}</h3>
            </div>
            <p className="text-white/75 text-sm leading-relaxed mb-3">
              {c.bespoke1}
            </p>
            <p className="text-white/70 text-sm leading-relaxed">
              {c.bespoke2}
            </p>
          </div>

          {/* Our Specialties */}
          <div className="bg-white/10 border border-white/15 rounded-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-bold text-white">{c.specialtiesTitle}</h3>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
              {c.specialties.map((item) => (
                <li key={item} className="flex items-start gap-2 text-white/80 text-sm">
                  <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px]">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bulk Production */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white/10 border border-white/15 rounded-2xl p-6 backdrop-blur-sm">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="text-lg font-bold text-white">{c.bulkTitle}</h3>
            </div>
            <p className="text-white/75 text-sm leading-relaxed">
              {c.bulkText}
            </p>
          </div>
          <div className="flex-shrink-0 text-center rounded-xl bg-white text-[var(--brand-primary-800)] px-5 py-3 shadow-lg">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-primary-500)]">{c.minLabel}</p>
            <p className="text-lg font-black leading-tight">{c.minValue}</p>
            <p className="text-[11px] text-[var(--brand-primary-600)]">{c.minUnit}</p>
          </div>
        </div>

        {/* Closing tagline */}
        <div className="text-center pt-2">
          <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            {c.closing}
          </p>
          <p
            className="mt-4 text-xl sm:text-2xl font-semibold text-white italic"
            style={{ fontFamily: "'Times New Roman', Serif" }}
          >
            {c.tagline}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Card;
